import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Row, Col, Card, Table, Button, Input, notification, Modal, Drawer,
  Select, Typography, Space, Tag, Tooltip, Progress, InputNumber
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  DeleteOutlined, FileExcelOutlined, FilterOutlined, EditOutlined, 
  ExclamationCircleOutlined, PlusOutlined, BarChartOutlined,
  SearchOutlined, ReloadOutlined, BookOutlined, DollarOutlined
} from "@ant-design/icons";
import { hasPermission } from '../../components/permissions';
import moment from 'moment';
import BudgetAllocationsForm from './Form';

const { confirm } = Modal;
const { Option } = Select;
const { Title, Text } = Typography;

function BudgetAllocationsList() {
  const history = useHistory();
  const [budgetList, setBudgetList] = useState([]);
  const [purposesList, setPurposesList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    fiscal_year: '',
    purpose_category: '',
    purpose_id: '',
    min_allocated_amount: '',
    max_allocated_amount: ''
  });

  const [purposeCategories, setPurposeCategories] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentBudgetId, setCurrentBudgetId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [showFilter, setShowFilter] = useState(true);

  const columns = [
    {
      title: '#',
      dataIndex: "key",
      key: "key",
      fixed: 'left',
      width: 60,
      align: 'center',
    },
    {
      title: 'Fiscal Year',
      dataIndex: "fiscal_year",
      key: "fiscal_year",
      fixed: 'left',
      width: 120,
      render: (year) => <Tag style={{ background: '#eef2f8', color: '#7a0000', border: 'none' }}>{year}</Tag>
    },
    {
      title: 'Purpose',
      key: 'purpose',
      width: 250,
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOutlined style={{ color: '#7a0000' }} />
            <Text strong>{record.purpose_name}</Text>
          </div>
          <div style={{ marginTop: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Code: {record.purpose_code}
            </Text>
            <br />
            <Tag color="orange" size="small">
              {record.purpose_category?.replace('_', ' ')}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Allocated Amount',
      dataIndex: 'allocated_amount',
      key: 'allocated_amount',
      width: 150,
      sorter: true,
      render: (amount) => (
        <Text strong style={{ color: '#7a0000', fontSize: '14px' }}>
          ₹{Number(amount).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Spent Amount',
      dataIndex: 'spent_amount',
      key: 'spent_amount',
      width: 150,
      render: (amount) => (
        <Text strong style={{ color: '#52c41a', fontSize: '14px' }}>
          ₹{Number(amount || 0).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Remaining Amount',
      dataIndex: 'remaining_amount',
      key: 'remaining_amount',
      width: 150,
      render: (amount) => (
        <Text strong style={{ color: '#fa8c16', fontSize: '14px' }}>
          ₹{Number(amount || 0).toLocaleString('en-IN')}
        </Text>
      )
    },
    {
      title: 'Utilization',
      dataIndex: 'utilization_percentage',
      key: 'utilization_percentage',
      width: 150,
      render: (percentage, record) => {
        const percent = parseFloat(percentage) || 0;
        let status = 'normal';
        let color = '#52c41a';
        
        if (percent > 90) {
          status = 'exception';
          color = '#ff4d4f';
        } else if (percent > 75) {
          status = 'active';
          color = '#fa8c16';
        }
        
        return (
          <div>
            <Progress 
              percent={Math.round(percent)} 
              size="small" 
              status={status}
              strokeColor={color}
              format={percent => `${percent}%`}
            />
            <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
              {percent.toFixed(1)}% utilized
            </Text>
          </div>
        );
      }
    },
    {
      title: 'Status',
      key: 'budget_status',
      width: 120,
      render: (_, record) => {
        const utilization = parseFloat(record.utilization_percentage) || 0;
        if (utilization > 90) {
          return <Tag color="error">Over Budget</Tag>;
        } else if (utilization > 75) {
          return <Tag color="warning">High Usage</Tag>;
        } else if (utilization > 0) {
          return <Tag color="success">On Track</Tag>;
        } else {
          return <Tag color="default">Not Started</Tag>;
        }
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => date ? moment(date).format('DD MMM YYYY') : '-'
    },
    {
      title: 'Actions',
      key: "action",
      dataIndex: "action",
      fixed: 'right',
      width: 120,
      align: 'center',
    }
  ];

  useEffect(() => {
    getBudgetList();
    loadPurposesList();
    loadPurposeCategories();
  }, []);

  const loadPurposeCategories = async () => {
    try {
      const res = await axios.get(`${baseUrl}/purpose-categories/list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      if (res.data.status) setPurposeCategories(res.data.result);
    } catch (_) {}
  };

  const loadPurposesList = async () => {
    try {
      const response = await axios.get(`${baseUrl}/purposes/list?limit=all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
      });
      setPurposesList(response.data.result || []);
    } catch (error) {
      console.error('Failed to load purposes:', error);
    }
  };

  const getBudgetList = async (page = 1, limit = 20, sortField = 'id', sortOrder = 'desc', search = '', filters = {}) => {
    try {
      setLoading(true);
      const sortParam = `&sortField=${sortField}&sortOrder=${sortOrder}`;
      const searchParam = search ? `&search=${search}` : '';
      const filterParams = Object.keys(filters)
        .filter(key => filters[key])
        .map(key => `${key}=${filters[key]}`)
        .join('&');
      
      const response = await axios.get(
        `${baseUrl}/budget-allocations/list?page=${page}&limit=${limit}${sortParam}${searchParam}&${filterParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          }
        }
      );
      
      if (response.data.status) {
        setBudgetList(response.data.result);
        setPagination({
          current: page,
          pageSize: limit,
          total: response.data.totalCount
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch budget allocations data',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${baseUrl}/budget-allocations/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        getBudgetList(pagination.current, pagination.pageSize, sortField, sortOrder, search, filters);
        notification.success({
          message: 'Success',
          description: 'Budget allocation deleted successfully!',
          placement: 'topRight'
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to delete budget allocation',
        placement: 'topRight'
      });
    }
  };

  const showDeleteConfirm = (id, purposeName, fiscalYear) => {
    confirm({
      title: 'Delete Budget Allocation',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: `Are you sure you want to delete budget allocation for "${purposeName}" in FY ${fiscalYear}?`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(id);
      },
    });
  };

  const showDrawer = (budgetId = null) => {
    setCurrentBudgetId(budgetId);
    setIsUpdateMode(!!budgetId);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setCurrentBudgetId(null);
    setIsUpdateMode(false);
  };

  const onRefresh = () => {
    closeDrawer();
    getBudgetList(currentPage, limit, sortField, sortOrder, search, filters);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const { current, pageSize } = pagination;
    const { field, order } = sorter;
    const formattedOrder = order === 'ascend' ? 'asc' : 'desc';
    
    setSortField(field);
    setSortOrder(formattedOrder);
    setCurrentPage(current);
    setLimit(pageSize);
    
    getBudgetList(current, pageSize, field, formattedOrder, search, filters);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleSearch = () => {
    getBudgetList(1, limit, sortField, sortOrder, search, filters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      fiscal_year: '',
      purpose_category: '',
      purpose_id: '',
      min_allocated_amount: '',
      max_allocated_amount: ''
    };
    setFilters(resetFilters);
    setSearch('');
    getBudgetList(1, limit, sortField, sortOrder, '', resetFilters);
  };

  const getFiscalYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    
    for (let i = -2; i <= 2; i++) {
      const year = currentYear + i;
      const fiscalYear = `${year}-${(year + 1).toString().slice(-2)}`;
      options.push(
        <Option key={fiscalYear} value={fiscalYear}>
          FY {fiscalYear}
        </Option>
      );
    }
    
    return options;
  };

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title={<span style={{ color: '#7a0000', fontWeight: 700, fontSize: 14 }}>Budget Allocations</span>}
            headStyle={{ borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Space size="middle">
                {hasPermission('/budget-allocations', 'createPermission') && (
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => showDrawer()}
                    style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', fontWeight: 600 }}
                  >
                    Add Budget Allocation
                  </Button>
                )}
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={() => {/* Export logic */}}
                  style={{ borderRadius: 8 }}
                >
                  Export Excel
                </Button>
              </Space>
            }
          >
            <div className="table-responsive">
              <Table
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                bordered
                size="middle"
                title={() => (
                  <div style={{ marginBottom: '16px' }}>
                    <Row gutter={[16, 16]} align="middle">
                      <Col>
                        <Button
                          icon={<FilterOutlined />}
                          onClick={() => setShowFilter(!showFilter)}
                          style={{ borderRadius: '6px' }}
                        >
                          {showFilter ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                      </Col>
                      <Col>
                        <Input
                          placeholder="Search by purpose name, code..."
                          value={search}
                          onChange={e => setSearch(e.target.value)}
                          onPressEnter={handleSearch}
                          style={{ width: 300, borderRadius: '6px' }}
                          suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
                        />
                      </Col>
                    </Row>

                    {showFilter && (
                      <div style={{
                        marginTop: '16px',
                        padding: '16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <Row gutter={[12, 12]}>
                          <Col span={6}>
                            <Select
                              placeholder="Fiscal Year"
                              value={filters.fiscal_year}
                              onChange={value => handleFilterChange('fiscal_year', value)}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              <Option value="">All Years</Option>
                              {getFiscalYearOptions()}
                            </Select>
                          </Col>
                          <Col span={6}>
                            <Select
                              placeholder="Purpose Category"
                              value={filters.purpose_category || undefined}
                              onChange={value => handleFilterChange('purpose_category', value || '')}
                              style={{ width: '100%' }}
                              allowClear
                            >
                              {purposeCategories.map(c => (
                                <Option key={c.id} value={c.name}>{c.name.replace(/_/g, ' ')}</Option>
                              ))}
                            </Select>
                          </Col>
                          <Col span={6}>
                            <InputNumber
                              placeholder="Min Allocated Amount"
                              value={filters.min_allocated_amount}
                              onChange={value => handleFilterChange('min_allocated_amount', value)}
                              style={{ width: '100%' }}
                              min={0}
                              formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={value => value.replace(/₹\s?|(,*)/g, '')}
                            />
                          </Col>
                          <Col span={6}>
                            <InputNumber
                              placeholder="Max Allocated Amount"
                              value={filters.max_allocated_amount}
                              onChange={value => handleFilterChange('max_allocated_amount', value)}
                              style={{ width: '100%' }}
                              min={0}
                              formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={value => value.replace(/₹\s?|(,*)/g, '')}
                            />
                          </Col>
                          <Col span={24}>
                            <Space>
                              <Button
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', borderRadius: '6px' }}
                              >
                                Search
                              </Button>
                              <Button
                                icon={<ReloadOutlined />}
                                onClick={handleResetFilters}
                                style={{ borderRadius: '6px' }}
                              >
                                Reset Filters
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </div>
                )}
                dataSource={budgetList.map((budget, index) => ({
                  key: (limit * (currentPage - 1)) + index + 1,
                  ...budget,
                  action: (
                    <Space size={4}>
                      {hasPermission('/budget-allocations', 'editPermission') && (
                        <Button size="small" icon={<EditOutlined />} onClick={() => showDrawer(budget.id)}
                          style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit</Button>
                      )}
                      {hasPermission('/budget-allocations', 'deletePermission') && (
                        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => showDeleteConfirm(budget.id, budget.purpose_name, budget.fiscal_year)}>Delete</Button>
                      )}
                    </Space>
                  )
                }))}
                pagination={{
                  ...pagination,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} budget allocations`,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                }}
                onChange={handleTableChange}
                summary={() => {
                  const totalAllocated = budgetList.reduce((sum, item) => sum + parseFloat(item.allocated_amount || 0), 0);
                  const totalSpent = budgetList.reduce((sum, item) => sum + parseFloat(item.spent_amount || 0), 0);
                  const totalRemaining = budgetList.reduce((sum, item) => sum + parseFloat(item.remaining_amount || 0), 0);
                  const avgUtilization = totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0;

                  return (
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                      <Table.Summary.Cell index={0} colSpan={3}><Text strong>Total</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Text strong style={{ color: '#7a0000' }}>
                          ₹{totalAllocated.toLocaleString('en-IN')}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong style={{ color: '#52c41a' }}>
                          ₹{totalSpent.toLocaleString('en-IN')}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong style={{ color: '#fa8c16' }}>
                          ₹{totalRemaining.toLocaleString('en-IN')}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong>{avgUtilization.toFixed(1)}%</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                      <Table.Summary.Cell></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Form Drawer */}
      <Drawer
        title={<span style={{ fontWeight: 700, color: '#7a0000' }}>{isUpdateMode ? "Update Budget Allocation" : "Add Budget Allocation"}</span>}
        width={720}
        open={drawerVisible}
        onClose={closeDrawer}
        destroyOnClose
      >
        <BudgetAllocationsForm
          budgetId={currentBudgetId}
          onRefreshData={onRefresh}
          purposesList={purposesList}
        />
      </Drawer>
    </div>
  );
}

export default BudgetAllocationsList;